import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  Calendar, 
  Clock, 
  Users, 
  FileText, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  RotateCcw,
  Activity,
  Shield,
  RefreshCw,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { rolloverAuditService } from '@/services/leave/rolloverAuditService';
import { format, formatDistanceToNow } from 'date-fns';

export function RolloverManagement() {
  // Get audit logs
  const { data: auditLogs = [], isLoading: isLoadingAuditLogs, refetch: refetchAuditLogs } = useQuery({
    queryKey: ['rollover-audit-logs'],
    queryFn: () => rolloverAuditService.getAuditLogs({ limit: 20 }),
  });

  // Get scheduled expirations
  const { data: upcomingExpirations = [] } = useQuery({
    queryKey: ['upcoming-expirations'],
    queryFn: () => rolloverAuditService.getScheduledExpirations({ 
      upcoming_days: 30,
      status: 'scheduled'
    }),
  });

  const getStatusBadge = (status: string) => {
    const variants = {
      'completed': { variant: 'default' as const, icon: CheckCircle, color: 'text-green-600' },
      'failed': { variant: 'destructive' as const, icon: XCircle, color: 'text-red-600' },
      'in_progress': { variant: 'secondary' as const, icon: Clock, color: 'text-blue-600' },
      'cancelled': { variant: 'outline' as const, icon: XCircle, color: 'text-muted-foreground' }
    };
    
    const config = variants[status as keyof typeof variants] || variants.in_progress;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className={`h-3 w-3 ${config.color}`} />
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const lastSuccessfulRun = auditLogs.find(
    (log) => log.status === 'completed' && log.operation_type === 'annual_rollover'
  );
  const lastFailedRun = auditLogs.find(
    (log) => log.status === 'failed' && log.operation_type === 'annual_rollover'
  );
  const recentRuns = auditLogs.filter((log) => log.operation_type === 'annual_rollover');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Leave Cycle Automation</h2>
          <p className="text-muted-foreground">
            Rollover and reset processing runs automatically based on your leave policy cycle settings.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetchAuditLogs()}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Automation Status Banner */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="rounded-full bg-primary/10 p-3">
              <Activity className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1 space-y-1">
              <h3 className="font-semibold">Automatic Processing Active</h3>
              <p className="text-sm text-muted-foreground">
                Leave balances are automatically rolled over or reset based on each policy's cycle anchor — 
                either on the employee's hire date anniversary or on a specific calendar date.
              </p>
              {lastSuccessfulRun && (
                <p className="text-sm text-muted-foreground mt-2">
                  Last successful run:{' '}
                  <span className="font-medium text-foreground">
                    {formatDistanceToNow(new Date(lastSuccessfulRun.completed_at || lastSuccessfulRun.started_at), { addSuffix: true })}
                  </span>
                  {' '}— processed {lastSuccessfulRun.employee_count} balance(s)
                  {' '}({lastSuccessfulRun.rollover_year} → {lastSuccessfulRun.target_year})
                </p>
              )}
              {!lastSuccessfulRun && !isLoadingAuditLogs && (
                <p className="text-sm text-muted-foreground mt-2">
                  No rollover runs recorded yet. The system will process eligible balances automatically.
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Failed Run Alert */}
      {lastFailedRun && (!lastSuccessfulRun || new Date(lastFailedRun.started_at) > new Date(lastSuccessfulRun.started_at)) && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Recent Rollover Failed</AlertTitle>
          <AlertDescription>
            <span>
              {(lastFailedRun.error_details as any)?.message || 'An error occurred during the last automatic rollover run.'}
              {' '}({format(new Date(lastFailedRun.started_at), 'PPp')})
            </span>
            <span className="block mt-1 text-xs">
              The scheduled job will retry automatically on its next run.
            </span>
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="status" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="status">Run History</TabsTrigger>
          <TabsTrigger value="expirations">Expirations</TabsTrigger>
          <TabsTrigger value="admin">Admin Actions</TabsTrigger>
        </TabsList>

        {/* Run History Tab */}
        <TabsContent value="status" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Rollover Run History
              </CardTitle>
              <CardDescription>
                Automatic and manual rollover processing events
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingAuditLogs ? (
                <div className="text-center py-8 text-muted-foreground">Loading run history...</div>
              ) : recentRuns.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No rollover runs recorded yet</p>
                  <p className="text-xs mt-1">The system will create entries here when rollover processing occurs.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentRuns.map((log) => {
                    const isAutomatic = (log.rollover_config as any)?.automated === true || (log.operation_summary as any)?.automatic === true;
                    return (
                      <div key={log.id} className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold">
                                  {log.rollover_year} → {log.target_year}
                                </h4>
                                <Badge variant="outline" className="text-xs">
                                  {isAutomatic ? (
                                    <><Activity className="h-3 w-3 mr-1" />Automatic</>
                                  ) : (
                                    <><Shield className="h-3 w-3 mr-1" />Manual</>
                                  )}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {format(new Date(log.started_at), 'PPpp')}
                              </p>
                            </div>
                          </div>
                          {getStatusBadge(log.status)}
                        </div>
                        
                        {log.status === 'completed' && (
                          <div className="grid grid-cols-3 gap-4 text-sm bg-muted/50 rounded-md p-3">
                            <div>
                              <span className="text-muted-foreground">Balances processed:</span>
                              <span className="ml-1 font-medium">{log.employee_count}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Duration:</span>
                              <span className="ml-1 font-medium">
                                {log.execution_duration_ms != null ? `${Math.round(log.execution_duration_ms / 1000)}s` : '—'}
                              </span>
                            </div>
                            {(log.operation_summary as any)?.skipped_count > 0 && (
                              <div>
                                <span className="text-muted-foreground">Skipped (already processed):</span>
                                <span className="ml-1 font-medium">{(log.operation_summary as any).skipped_count}</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {log.status === 'failed' && log.error_details && (
                          <Alert variant="destructive">
                            <XCircle className="h-4 w-4" />
                            <AlertTitle>Error Details</AlertTitle>
                            <AlertDescription>
                              {(log.error_details as any)?.message || 'Unknown error occurred'}
                            </AlertDescription>
                          </Alert>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expirations Tab */}
        <TabsContent value="expirations" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Upcoming Balance Expirations
              </CardTitle>
              <CardDescription>
                Carry-over balances scheduled to expire in the next 30 days
              </CardDescription>
            </CardHeader>
            <CardContent>
              {upcomingExpirations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No scheduled expirations in the next 30 days</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingExpirations.map((expiration) => (
                    <div key={expiration.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold">{expiration.employee_name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {expiration.policy_name} — {expiration.carried_over_days} days expire on {format(new Date(expiration.expiration_date), 'PP')}
                          </p>
                        </div>
                        <Badge variant="outline">
                          {expiration.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Admin Actions Tab */}
        <TabsContent value="admin" className="space-y-6">
          <Alert>
            <Shield className="h-4 w-4" />
            <AlertTitle>Processing is system-managed</AlertTitle>
            <AlertDescription>
              Rollover and accrual processing can only be executed by the scheduled background job.
              Manual triggering from the app has been disabled to prevent duplicate or cross-company
              processing.
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RotateCcw className="h-5 w-5" />
                Rollover Schedule
              </CardTitle>
              <CardDescription>
                The rollover job runs daily and processes only balances whose leave cycle has ended.
                Idempotency guards prevent the same balance or accrual release from being credited twice.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-md bg-muted/50 p-4 text-sm space-y-2">
                <h4 className="font-medium">How automatic processing works</h4>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>The system checks daily for employees whose leave cycle has ended</li>
                  <li>For <strong>hire date</strong> policies, rollover occurs on each employee's hire anniversary</li>
                  <li>For <strong>specific date</strong> policies, rollover occurs on the configured calendar date</li>
                  <li><strong>Fixed-accrual</strong> policies use their own cycle-aware engine — rollover/reset happens automatically at each cycle boundary based on the release schedule</li>
                  <li>Non-fixed policies use the year-based annual rollover path</li>
                  <li>Each balance is only processed once per day (idempotency protected)</li>
                  <li>Results are logged automatically in the Run History tab</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
